import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Collapse,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Divider,
} from "@mhome/ui";
import { CircularProgress } from "@mui/material";
import {
  Delete as DeleteIcon,
  Nfc as NfcIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  EditOff as EditOffIcon,
} from "@mui/icons-material";
import { useRecoilState, useRecoilValue } from "recoil";
import { activeScopeState, wsReadyState } from "../global-state";
import { useWebSocket } from "../ws/websocket";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import {
  listNfcTags,
  createNfcTag,
  activateNfcTag,
  deleteNfcTag,
  listNfcSources,
  updateNfcTag,
} from "../service/nfc-service";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import dayjs from "dayjs";
import { useCustomFormat } from "../views/after-init";
import { useToast } from "../common/toast";
import { useAppInterface } from "../app/app-interface-provider";
import NfcWriteDialog from "./nfc-write-dialog";
import NfcCheckDialog from "./nfc-check-dialog";
import { spacingToPx, convertSxToStyle } from "../lib/utils";

const NfcView = forwardRef((props, ref) => {
  const wsReady = useRecoilValue(wsReadyState);
  const { sendAndWaitWs } = useWebSocket();
  const [activeScope] = useRecoilState(activeScopeState);
  const { successToast, errorToast } = useToast();
  const customFormat = useCustomFormat();
  const getComponentBgColor = useComponentBackgroundColor();
  const {
    enterNfcWrite,
    cancelNfcWrite,
    platform,
    enterNfcCheck,
    cancelNfcCheck,
  } = useAppInterface();
  const [nfcTags, setNfcTags] = useState([]);
  const [trustedSources, setTrustedSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [showWriteDialog, setShowWriteDialog] = useState(false);
  const [writingNfc, setWritingNfc] = useState(false);
  const [writeMode, setWriteMode] = useState(false); // New state for write mode
  const [duplicateMode, setDuplicateMode] = useState(false); // New state for duplication mode
  const [duplicateTagName, setDuplicateTagName] = useState(""); // Store name of tag being duplicated
  const [newTagName, setNewTagName] = useState("");
  const [newTagDescription, setNewTagDescription] = useState("");
  const [newTagPolicy, setNewTagPolicy] = useState("ALL_PHONE");
  const [newTagTrustedSources, setNewTagTrustedSources] = useState([]);
  const [editingCards, setEditingCards] = useState({});
  const [savingCards, setSavingCards] = useState({});
  const pendingTagDataRef = useRef(null); // Store tag data for write completion
  const [nameError, setNameError] = useState(""); // Error message for name validation
  // New state for check functionality
  const [showCheckDialog, setShowCheckDialog] = useState(false);
  const [checkingNfc, setCheckingNfc] = useState(false);
  const [checkMode, setCheckMode] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkError, setCheckError] = useState(null);

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Countdown timer state
  const [countdown, setCountdown] = useState(60);
  const [countdownActive, setCountdownActive] = useState(false);

  // Policy constants
  const POLICY_ALL_PHONE = "ALL_PHONE";
  const POLICY_CUSTOM_PHONE = "CUSTOM_PHONE";

  // Expose handleAdd and handleQuery methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleAdd: handleNewNfcTag,
    handleQuery: handleCheckNfcTag,
  }));

  useEffect(() => {
    if (wsReady) {
      fetchNfcTags();
      fetchTrustedSources();
    }
  }, [wsReady]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  // Handle countdown timeout
  const handleCountdownTimeout = useCallback(() => {
    setCountdownActive(false);
    setCountdown(60);
    if (writeMode || duplicateMode) {
      handleCloseWriteDialog();
    } else if (checkMode) {
      handleCloseCheckDialog();
    }
  }, [writeMode, duplicateMode, checkMode]);

  // Countdown timer effect
  useEffect(() => {
    let intervalId;

    if (countdownActive && countdown > 0) {
      intervalId = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            handleCountdownTimeout();
            return 60; // Reset for next time
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [countdownActive, countdown, handleCountdownTimeout]);

  // Set up NFC write event handlers
  useEffect(() => {
    const handleNfcWriteSuccess = async () => {
      console.log("NFC write successful");
      setWritingNfc(false);
      setWriteMode(false);

      if (pendingTagDataRef.current) {
        try {
          // Activate the NFC tag after successful write
          const activateResponse = await activateNfcTag(
            sendAndWaitWs,
            pendingTagDataRef.current.tagId
          );
          if (activateResponse.error) {
            throw new Error(activateResponse.error);
          }
          successToast("NFC tag written and activated successfully");
        } catch (error) {
          console.warn(
            "Failed to activate tag, but NFC write succeeded:",
            error
          );
          successToast("NFC tag written, but activation in service failed");
        }

        setShowWriteDialog(false);

        // Stop countdown timer
        setCountdownActive(false);
        setCountdown(60);

        // Reset form fields
        setNewTagName("");
        setNewTagDescription("");
        setNewTagPolicy(POLICY_ALL_PHONE);
        setNewTagTrustedSources([]);
        setNameError("");
        pendingTagDataRef.current = null;
        // Refresh the list
        fetchNfcTags();
      }
    };

    const handleNfcWriteError = async (error) => {
      console.error("NFC write error:", error);
      setWritingNfc(false);
      setWriteMode(false);

      // Delete the created tag if NFC write failed
      if (pendingTagDataRef.current) {
        try {
          await deleteNfcTag(sendAndWaitWs, pendingTagDataRef.current.tagId);
          console.log("Successfully deleted failed NFC tag");
        } catch (deleteError) {
          console.warn(
            "Failed to delete created tag after write error:",
            deleteError
          );
        }
      }

      pendingTagDataRef.current = null;
      // Stop countdown timer
      setCountdownActive(false);
      setCountdown(60);
      errorToast(error || "Failed to write NFC tag");
    };

    // Set up global event handlers
    window.onNfcWriteSuccess = handleNfcWriteSuccess;
    window.onNfcWriteError = handleNfcWriteError;

    // Cleanup on unmount
    return () => {
      window.onNfcWriteSuccess = null;
      window.onNfcWriteError = null;
    };
  }, [successToast, errorToast, POLICY_ALL_PHONE]);

  // Set up NFC check event handlers
  useEffect(() => {
    const handleNfcCheckSuccess = (data) => {
      console.log("NFC check successful:", data);
      setCheckingNfc(false);
      // Don't exit check mode - keep it active for multiple checks

      // Validate the format: should be "mhomeai://nfc/scopeId:tagId"
      if (typeof data !== "string") {
        setCheckResult(null);
        setCheckError("Not a mHome NFC tag");
        return;
      }

      const formatRegex = /^mhomeai:\/\/nfc\/(.+):(.+)$/;
      const match = data.match(formatRegex);

      if (!match) {
        setCheckResult(null);
        setCheckError("Not a mHome NFC tag");
        return;
      }

      const [, scopeId, tagId] = match;

      // Check if scope matches current active scope
      if (scopeId !== activeScope) {
        setCheckResult(null);
        setCheckError("Tag for another space");
        return;
      }

      // Check if tag exists in current list
      const foundTag = nfcTags.find((tag) => tag.id === tagId);
      if (foundTag) {
        setCheckResult(`✓ ${foundTag.name}`);
        setCheckError(null);
      } else {
        setCheckResult(null);
        setCheckError(
          "The tag belongs to this space, but seems no longer in use"
        );
      }

      // Reset countdown timer for next check
      setCountdown(60);
      setCountdownActive(true);
    };

    const handleNfcCheckError = (error) => {
      console.error("NFC check error:", error);
      setCheckingNfc(false);
      // Don't exit check mode - keep it active for multiple checks
      setCheckResult(null);
      setCheckError(error || "Failed to check NFC tag");

      // Reset countdown timer for next check
      setCountdown(60);
      setCountdownActive(true);
    };

    // Set up global event handlers for check
    window.onNfcCheckSuccess = handleNfcCheckSuccess;
    window.onNfcCheckError = handleNfcCheckError;

    // Cleanup on unmount
    return () => {
      window.onNfcCheckSuccess = null;
      window.onNfcCheckError = null;
    };
  }, [activeScope, nfcTags]);

  const fetchTrustedSources = async () => {
    try {
      const response = await listNfcSources(sendAndWaitWs);
      // Filter only phone sources and format the data
      const phoneSourcesData = Array.isArray(response?.sources)
        ? response.sources
            .filter((source) => source.sourceType === "phone")
            .map((source) => ({
              sourceId: source.sourceId,
              sourceName: source.sourceName || `Phone ${source.sourceId}`,
              sourceType: source.sourceType,
            }))
        : [];
      setTrustedSources(phoneSourcesData);
    } catch (error) {
      console.error("Failed to fetch trusted sources:", error);
      errorToast("Failed to fetch available phones");
    }
  };

  const fetchNfcTags = async () => {
    setLoading(true);
    try {
      const response = await listNfcTags(sendAndWaitWs);
      // Response structure: { nfcTags: [NfcTag] }
      let nfcTagsData = Array.isArray(response?.nfcTags)
        ? response.nfcTags.map((tag) => ({
            id: tag.tagId,
            name: tag.name || `NFC Tag ${tag.tagId}`,
            description: tag.description || "",
            scopeId: tag.scopeId,
            createdAt: tag.createdAt || Date.now(),
            lastUsedAt: tag.lastUsedAt || 0,
            policy: tag.policy || POLICY_ALL_PHONE,
            trustedSources: tag.trustedSources || [],
          }))
        : [];

      setNfcTags(nfcTagsData);
    } catch (error) {
      console.error("Failed to fetch NFC tags:", error);
      errorToast("Failed to fetch NFC tags");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNfcTag = async (event, tagId) => {
    event.stopPropagation();
    setTagToDelete(tagId);
    setShowDeleteDialog(true);
  };

  const handleConfirmDeleteNfcTag = async () => {
    if (!tagToDelete) return;
    try {
      const success = await deleteNfcTag(sendAndWaitWs, tagToDelete);
      if (success) {
        successToast("NFC tag deleted");
        setNfcTags((prev) => prev.filter((tag) => tag.id !== tagToDelete));
      } else {
        errorToast("Failed to delete NFC tag");
      }
    } catch (error) {
      console.error("Failed to delete NFC tag:", error);
      errorToast("Failed to delete NFC tag");
    } finally {
      setShowDeleteDialog(false);
      setTagToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setTagToDelete(null);
  };

  const handleNewNfcTag = async () => {
    const currentPlatform = platform();
    if (currentPlatform !== "android" && currentPlatform !== "ios") {
      errorToast("NFC tag write is only supported in Android/iOS App");
      return;
    }

    // Reset form fields
    setNewTagName("");
    setNewTagDescription("");
    setNewTagPolicy(POLICY_ALL_PHONE);
    setNewTagTrustedSources([]);
    setNameError("");
    setShowWriteDialog(true);
  };

  const handleDuplicateNfcTag = async (event, nfcTag) => {
    event.stopPropagation();
    const currentPlatform = platform();
    if (currentPlatform !== "android" && currentPlatform !== "ios") {
      errorToast("NFC tag write is only supported in Android/iOS App");
      return;
    }

    // Set up duplication mode and open dialog
    setDuplicateMode(true);
    setDuplicateTagName(nfcTag.name);
    setWritingNfc(true);
    setShowWriteDialog(true);

    try {
      // Use the same data format: "mhomeai://nfc/scopeId:tagId"
      const tagData = `mhomeai://nfc/${activeScope}:${nfcTag.id}`;
      const result = await enterNfcWrite(tagData);

      if (result.success) {
        // Successfully entered write mode for duplication
        setWriteMode(true);
        setWritingNfc(false);
        // Start countdown timer
        setCountdown(60);
        setCountdownActive(true);

        // Set up temporary handlers for duplication
        const originalOnSuccess = window.onNfcWriteSuccess;
        const originalOnError = window.onNfcWriteError;

        window.onNfcWriteSuccess = () => {
          successToast(`NFC tag "${nfcTag.name}" duplicated successfully`);
          setShowWriteDialog(false);
          setDuplicateMode(false);
          setWriteMode(false);
          setDuplicateTagName("");
          // Stop countdown timer
          setCountdownActive(false);
          setCountdown(60);
          // Refresh the list
          fetchNfcTags();
          // Restore original handlers
          window.onNfcWriteSuccess = originalOnSuccess;
          window.onNfcWriteError = originalOnError;
        };

        window.onNfcWriteError = (error) => {
          errorToast(error || "Failed to duplicate NFC tag");
          setDuplicateMode(false);
          setWriteMode(false);
          setDuplicateTagName("");
          // Stop countdown timer
          setCountdownActive(false);
          setCountdown(60);
          // Restore original handlers
          window.onNfcWriteSuccess = originalOnSuccess;
          window.onNfcWriteError = originalOnError;
        };
      } else {
        setWritingNfc(false);
        setDuplicateMode(false);
        setShowWriteDialog(false);
        setDuplicateTagName("");
        errorToast(result.error || "Failed to enter duplicate mode");
      }
    } catch (error) {
      console.error("Failed to duplicate NFC tag:", error);
      setWritingNfc(false);
      setDuplicateMode(false);
      setShowWriteDialog(false);
      setDuplicateTagName("");
      errorToast("Failed to duplicate NFC tag");
    }
  };

  const handleStartNfcWrite = async () => {
    // Clear previous error
    setNameError("");

    // Validate required name field
    if (!newTagName.trim()) {
      setNameError("Name is required");
      return;
    }

    // Check for duplicate name
    const trimmedName = newTagName.trim();
    const duplicateName = nfcTags.some(
      (tag) => tag.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (duplicateName) {
      setNameError("An NFC tag with this name already exists");
      return;
    }

    // Validate policy-specific requirements
    if (
      newTagPolicy === POLICY_CUSTOM_PHONE &&
      newTagTrustedSources.length === 0
    ) {
      errorToast("Please select at least one phone for custom policy");
      return;
    }

    setWritingNfc(true);
    try {
      // First create the NFC tag via API to get the tagId
      const nfcTagData = {
        name: newTagName.trim(),
        description: newTagDescription.trim(),
        policy: newTagPolicy,
        trustedSources:
          newTagPolicy === POLICY_ALL_PHONE ? [] : newTagTrustedSources,
      };

      const createResponse = await createNfcTag(sendAndWaitWs, nfcTagData);

      if (!createResponse || createResponse.error || !createResponse.tagId) {
        setWritingNfc(false);
        if (createResponse?.error === "BAD_REQUEST") {
          errorToast("Invalid or duplicate NFC tag name");
        } else {
          errorToast(createResponse?.error || "Failed to create NFC tag");
        }
        return;
      }

      const tagId = createResponse.tagId;
      // Use the new data format: "mhomeai://nfc/scopeId:tagId"
      const tagData = `mhomeai://nfc/${activeScope}:${tagId}`;

      // Store the tag data for when write succeeds
      pendingTagDataRef.current = {
        ...nfcTagData,
        tagId: tagId,
        createdAt: Date.now(),
        lastUsedAt: 0,
      };

      const result = await enterNfcWrite(tagData);

      if (result.success) {
        // Successfully entered write mode, now wait for user to scan NFC tag
        setWriteMode(true);
        setWritingNfc(false); // No longer "writing", just waiting for user action
        // Start countdown timer
        setCountdown(60);
        setCountdownActive(true);
      } else {
        // Failed to enter write mode, delete the created tag
        try {
          await deleteNfcTag(sendAndWaitWs, tagId);
        } catch (deleteError) {
          console.warn("Failed to cleanup created tag:", deleteError);
        }
        setWritingNfc(false);
        pendingTagDataRef.current = null;
        errorToast(result.error || "Failed to enter NFC write mode");
      }
    } catch (error) {
      console.error("Failed to create NFC tag or enter write mode:", error);
      setWritingNfc(false);
      pendingTagDataRef.current = null;
      errorToast("Failed to create NFC tag");
    }
  };

  const handleCloseWriteDialog = async () => {
    if (writingNfc || writeMode) {
      // User is manually closing during write mode, cancel the NFC write
      try {
        await cancelNfcWrite();
        console.log("NFC write cancelled");
      } catch (error) {
        console.error("Failed to cancel NFC write:", error);
      }

      // Delete the created tag if user cancels
      if (pendingTagDataRef.current) {
        try {
          await deleteNfcTag(sendAndWaitWs, pendingTagDataRef.current.tagId);
          console.log("Successfully deleted cancelled NFC tag");
        } catch (deleteError) {
          console.warn(
            "Failed to delete created tag after cancellation:",
            deleteError
          );
        }
      }
    }

    setShowWriteDialog(false);
    setWriteMode(false);
    setWritingNfc(false);
    pendingTagDataRef.current = null;
    setDuplicateMode(false);
    setDuplicateTagName("");
    setNameError("");
    // Stop countdown timer
    setCountdownActive(false);
    setCountdown(60);
  };

  const handleToggleEditing = (tagId) => {
    setEditingCards((prev) => ({
      ...prev,
      [tagId]: !prev[tagId],
    }));
  };

  const handlePolicyChange = (tagId, newPolicy) => {
    setNfcTags((prev) =>
      prev.map((tag) =>
        tag.id === tagId
          ? {
              ...tag,
              policy: newPolicy,
              trustedSources:
                newPolicy === POLICY_ALL_PHONE ? [] : tag.trustedSources,
            }
          : tag
      )
    );
  };

  const handleTrustedSourceToggle = (tagId, sourceId) => {
    setNfcTags((prev) =>
      prev.map((tag) => {
        if (tag.id === tagId) {
          const currentSources = tag.trustedSources || [];
          const sourceExists = currentSources.some(
            (source) => source.sourceId === sourceId
          );
          const trustedSource = trustedSources.find(
            (source) => source.sourceId === sourceId
          );

          if (sourceExists) {
            return {
              ...tag,
              trustedSources: currentSources.filter(
                (source) => source.sourceId !== sourceId
              ),
            };
          } else if (trustedSource) {
            return {
              ...tag,
              trustedSources: [...currentSources, trustedSource],
            };
          }
        }
        return tag;
      })
    );
  };

  const handleSaveNfcTag = async (tag) => {
    setSavingCards((prev) => ({ ...prev, [tag.id]: true }));
    try {
      const tagData = {
        tagId: tag.id,
        name: tag.name,
        description: tag.description,
        policy: tag.policy,
        trustedSources: tag.trustedSources,
        createdAt: tag.createdAt,
        lastUsedAt: tag.lastUsedAt,
      };

      const response = await updateNfcTag(sendAndWaitWs, tagData);
      if (response && !response.error) {
        successToast("NFC tag updated successfully");
        setEditingCards((prev) => ({ ...prev, [tag.id]: false }));
      } else {
        errorToast("Failed to update NFC tag");
      }
    } catch (error) {
      console.error("Failed to update NFC tag:", error);
      errorToast("Failed to update NFC tag");
    } finally {
      setSavingCards((prev) => ({ ...prev, [tag.id]: false }));
    }
  };

  const handleTagFieldChange = (tagId, field, value) => {
    setNfcTags((prev) =>
      prev.map((tag) => (tag.id === tagId ? { ...tag, [field]: value } : tag))
    );
  };

  const handleNewTagPolicyChange = (newPolicy) => {
    setNewTagPolicy(newPolicy);
    if (newPolicy === POLICY_ALL_PHONE) {
      setNewTagTrustedSources([]);
    }
  };

  const handleNewTagTrustedSourceToggle = (sourceId) => {
    const sourceExists = newTagTrustedSources.some(
      (source) => source.sourceId === sourceId
    );
    const trustedSource = trustedSources.find(
      (source) => source.sourceId === sourceId
    );

    if (sourceExists) {
      setNewTagTrustedSources((prev) =>
        prev.filter((source) => source.sourceId !== sourceId)
      );
    } else if (trustedSource) {
      setNewTagTrustedSources((prev) => [...prev, trustedSource]);
    }
  };

  const handleNameChange = (value) => {
    setNewTagName(value);
    // Clear error when user starts typing
    if (nameError) {
      setNameError("");
    }
  };

  const handleCheckNfcTag = async () => {
    const currentPlatform = platform();
    if (currentPlatform !== "android" && currentPlatform !== "ios") {
      errorToast("NFC tag check is only supported in Android/iOS App");
      return;
    }

    // Reset check state
    setCheckResult(null);
    setCheckError(null);
    setCheckingNfc(true);
    setShowCheckDialog(true);

    try {
      const result = await enterNfcCheck();

      if (result.success) {
        setCheckMode(true);
        setCheckingNfc(false);
        // Start countdown timer
        setCountdown(60);
        setCountdownActive(true);
      } else {
        setCheckingNfc(false);
        setShowCheckDialog(false);
        errorToast(result.error || "Failed to enter NFC check mode");
      }
    } catch (error) {
      console.error("Failed to enter NFC check mode:", error);
      setCheckingNfc(false);
      setShowCheckDialog(false);
      errorToast("Failed to enter NFC check mode");
    }
  };

  const handleCloseCheckDialog = async () => {
    if (checkingNfc || checkMode) {
      // User is manually closing during check mode, cancel the NFC check
      try {
        await cancelNfcCheck();
        console.log("NFC check cancelled");
      } catch (error) {
        console.error("Failed to cancel NFC check:", error);
      }
    }

    setShowCheckDialog(false);
    setCheckMode(false);
    setCheckingNfc(false);
    setCheckResult(null);
    setCheckError(null);
    // Stop countdown timer
    setCountdownActive(false);
    setCountdown(60);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: spacingToPx(3),
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div
      style={{
        flexGrow: 1,
        margin: spacingToPx(2),
        paddingBottom: spacingToPx(12),
      }}
    >
      {nfcTags.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
            padding: spacingToPx(3),
          }}
        >
          <NfcIcon
            className="text-muted-foreground"
            style={{
              fontSize: 48,
              marginBottom: spacingToPx(2),
            }}
          />
          <Typography
            variant="h6"
            className="text-muted-foreground"
            style={{
              marginBottom: spacingToPx(1),
            }}
          >
            No NFC Tags
          </Typography>
          <Typography variant="body1" className="text-muted-foreground">
            Create your first NFC tag using the add button above
          </Typography>
        </div>
      ) : (
        <ResponsiveCardGrid spacing={2} breakpoints={{ xs: 1, sm: 2, md: 3 }}>
          {nfcTags.map((nfcTag, index) => (
            <Card
              key={nfcTag.id}
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                boxShadow: "none",
              }}
            >
              <CardContent
                style={{
                  padding: spacingToPx(1.5),
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: spacingToPx(1),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <NfcIcon
                      className="text-primary"
                      style={{
                        marginRight: spacingToPx(1),
                      }}
                    />
                    {editingCards[nfcTag.id] ? (
                      <TextField
                        value={nfcTag.name}
                        onChange={(e) =>
                          handleTagFieldChange(
                            nfcTag.id,
                            "name",
                            e.target.value
                          )
                        }
                        variant="outlined"
                        size="small"
                        style={{ flex: 1 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <Typography
                        variant="body1"
                        component="div"
                        style={{
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontWeight: 500,
                        }}
                      >
                        {nfcTag.name}
                      </Typography>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: spacingToPx(0.5) }}>
                    {editingCards[nfcTag.id] ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleEditing(nfcTag.id);
                          }}
                          className="text-muted-foreground"
                        >
                          <EditOffIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveNfcTag(nfcTag);
                          }}
                          disabled={savingCards[nfcTag.id]}
                          className="text-primary"
                        >
                          {savingCards[nfcTag.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon />
                          )}
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleEditing(nfcTag.id);
                          }}
                          className="text-primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDuplicateNfcTag(e, nfcTag)}
                          className="text-primary"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteNfcTag(e, nfcTag.id)}
                          className="text-destructive"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </div>
                </div>

                {editingCards[nfcTag.id] && (
                  <TextField
                    value={nfcTag.description}
                    onChange={(e) =>
                      handleTagFieldChange(
                        nfcTag.id,
                        "description",
                        e.target.value
                      )
                    }
                    label="Description"
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    style={{ marginBottom: spacingToPx(1) }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}

                {!editingCards[nfcTag.id] && (
                  <>
                    {/* Policy Display */}
                    <div style={{ marginBottom: spacingToPx(1) }}>
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 500,
                          marginBottom: spacingToPx(0.25),
                        }}
                      >
                        Policy:{" "}
                        <span className="text-primary">
                          {nfcTag.policy === POLICY_ALL_PHONE
                            ? "All Phones"
                            : "Custom Phones"}
                        </span>
                      </Typography>
                      {nfcTag.policy === POLICY_CUSTOM_PHONE &&
                        nfcTag.trustedSources?.length > 0 && (
                          <Typography
                            variant="caption"
                            className="text-muted-foreground"
                          >
                            Allowed sources:{" "}
                            {nfcTag.trustedSources
                              .map((source) => source.sourceName)
                              .join(", ")}
                          </Typography>
                        )}
                    </div>
                  </>
                )}

                {editingCards[nfcTag.id] && (
                  <>
                    <Divider style={{ marginBottom: spacingToPx(1) }} />

                    <div style={{ marginBottom: spacingToPx(1) }}>
                      <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 500 }}
                      >
                        Configuration
                      </Typography>
                    </div>

                    {/* Policy Selection */}
                    <FormControl
                      component="fieldset"
                      style={{ marginBottom: spacingToPx(1) }}
                    >
                      <FormLabel
                        component="legend"
                        style={{ fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        Access Policy
                      </FormLabel>
                      <RadioGroup
                        value={nfcTag.policy}
                        onChange={(e) => {
                          handlePolicyChange(nfcTag.id, e.target.value);
                        }}
                        style={{ marginTop: spacingToPx(1) }}
                      >
                        <FormControlLabel
                          value={POLICY_ALL_PHONE}
                          control={<Radio size="small" />}
                          label="All Phones"
                          style={{ height: 28 }}
                        />
                        <FormControlLabel
                          value={POLICY_CUSTOM_PHONE}
                          control={<Radio size="small" />}
                          label="Custom Phones"
                          style={{ height: 28 }}
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* Trusted Sources Selection (only for CUSTOM_PHONE policy) */}
                    {nfcTag.policy === POLICY_CUSTOM_PHONE && (
                      <FormControl
                        component="fieldset"
                        style={{ marginBottom: spacingToPx(1) }}
                      >
                        <FormLabel
                          component="legend"
                          style={{ fontSize: "0.875rem", fontWeight: 500 }}
                        >
                          Trusted Phones
                        </FormLabel>
                        {trustedSources.length === 0 ? (
                          <Typography
                            variant="body2"
                            className="text-muted-foreground"
                            style={{
                              marginTop: spacingToPx(1),
                            }}
                          >
                            No phones available
                          </Typography>
                        ) : (
                          <FormGroup style={{ marginTop: spacingToPx(1) }}>
                            {trustedSources.map((source) => (
                              <FormControlLabel
                                key={source.sourceId}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={
                                      nfcTag.trustedSources?.some(
                                        (ts) => ts.sourceId === source.sourceId
                                      ) || false
                                    }
                                    onChange={() => {
                                      handleTrustedSourceToggle(
                                        nfcTag.id,
                                        source.sourceId
                                      );
                                    }}
                                  />
                                }
                                label={source.sourceName}
                                style={{ height: 28 }}
                              />
                            ))}
                          </FormGroup>
                        )}
                      </FormControl>
                    )}
                  </>
                )}

                <div style={{ marginTop: "auto" }}>
                  {!editingCards[nfcTag.id] && (
                    <>
                      {nfcTag.description && (
                        <Typography
                          variant="body2"
                          component="div"
                          className="text-muted-foreground"
                          style={{
                            marginBottom: spacingToPx(0.5),
                            fontSize: "0.8rem",
                          }}
                        >
                          {nfcTag.description}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        component="div"
                        className="text-muted-foreground"
                        style={{
                          fontSize: "0.7rem",
                        }}
                      >
                        Last used:{" "}
                        {nfcTag.lastUsedAt > 0
                          ? customFormat(dayjs(nfcTag.lastUsedAt), currentTime)
                          : "Never used"}
                      </Typography>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </ResponsiveCardGrid>
      )}

      {/* NFC Write Dialog */}
      <NfcWriteDialog
        open={showWriteDialog}
        onClose={handleCloseWriteDialog}
        duplicateMode={duplicateMode}
        duplicateTagName={duplicateTagName}
        writeMode={writeMode}
        newTagName={newTagName}
        writingNfc={writingNfc}
        newTagDescription={newTagDescription}
        newTagPolicy={newTagPolicy}
        handleNameChange={handleNameChange}
        nameError={nameError}
        setNewTagDescription={setNewTagDescription}
        handleNewTagPolicyChange={handleNewTagPolicyChange}
        trustedSources={trustedSources}
        newTagTrustedSources={newTagTrustedSources}
        handleNewTagTrustedSourceToggle={handleNewTagTrustedSourceToggle}
        handleStartNfcWrite={handleStartNfcWrite}
        countdown={countdown}
        countdownActive={countdownActive}
      />

      {/* NFC Check Dialog */}
      <NfcCheckDialog
        open={showCheckDialog}
        onClose={handleCloseCheckDialog}
        checkMode={checkMode}
        checkingNfc={checkingNfc}
        checkResult={checkResult}
        checkError={checkError}
        countdown={countdown}
        countdownActive={countdownActive}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        variant="pops"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the NFC tag &quot;
            {nfcTags.find((tag) => tag.id === tagToDelete)?.name}&quot;? The tag
            data is not removed. But it&apos;s no longer recognized by the
            service.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteNfcTag}
            color="error"
            variant="contained"
            style={{ boxShadow: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

NfcView.displayName = "NfcView";

export default NfcView;
